import React, { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import {
  ArrowRight, Users, Wallet, ShieldCheck, Fish, Sparkles,
  Calculator, Repeat, Fuel, Mail, Loader2,
  Zap, Radio, TrendingUp, TrendingDown, MessageCircle, Flame,
  LineChart, Newspaper, BarChart2,
} from "lucide-react";
import { toast } from "sonner";
import AdBanner, { ADS_ENABLED } from "../components/AdBanner";
import DailyCall from "../components/DailyCall";

/* ============================================================
   SHARED HELPERS
============================================================ */
function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(28px)", transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s` }}>
      {children}
    </div>
  );
}

function useCountUp(target, duration = 1400) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting && !started) setStarted(true); }, { threshold: 0.3 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [started, target]);
  useEffect(() => {
    if (!started || target == null) return;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setVal(Math.floor(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [started, target, duration]);
  return [ref, val];
}

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (diff < 60) return diff + "m ago";
  if (diff < 1440) return Math.floor(diff / 60) + "h ago";
  return Math.floor(diff / 1440) + "d ago";
}

const CATEGORY_GRADIENTS = {
  bitcoin: "linear-gradient(135deg, #f59e0b2e, #0a0a09)",
  ethereum: "linear-gradient(135deg, #8b5cf62e, #0a0a09)",
  regulation: "linear-gradient(135deg, #64748b2e, #0a0a09)",
  defi: "linear-gradient(135deg, #3b82f62e, #0a0a09)",
  altcoins: "linear-gradient(135deg, #14b8a62e, #0a0a09)",
  "market-analysis": "linear-gradient(135deg, #ef44442e, #0a0a09)",
};
function Thumb({ n, className }) {
  if (n.image_url) return <img src={n.image_url} alt={n.title} className={className + " object-cover"} />;
  return <div className={className} style={{ background: CATEGORY_GRADIENTS[n.category_slug] || "linear-gradient(135deg,#1a1a17,#0a0a09)" }} />;
}

/* ============================================================
   HERO  ->  bleeds directly into AI SECTION (shared background)
============================================================ */
function FloatingCard({ label, value, sub, positive, style }) {
  return (
    <div className="absolute bg-[var(--bg-base)]/60 backdrop-blur-md border border-[var(--border-soft)] rounded-2xl px-4 py-3 shadow-[0_20px_50px_rgba(0,0,0,0.6)]"
      style={{ animation: "floatY 6s ease-in-out infinite", ...style }}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">{label}</div>
      <div className="font-display font-bold text-lgtext-[var(--text-primary)] mt-0.5">{value}</div>
      {sub && <div className={"text-xs font-mono mt-0.5 " + (positive ? "text-[var(--bullish)]" : "text-[var(--bearish)]")}>{sub}</div>}
    </div>
  );
}




function Hero() {
  return (
    <section className="relative min-h-[78vh] flex items-center pt-8 bg-[var(--bg-base)]">
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url('/images/world-map-hero.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center top",
            opacity: 0.42,
            filter: "brightness(1.3) saturate(1.4)",
            maskImage: "linear-gradient(to bottom, black 0%, black 65%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 65%, transparent 100%)",
            animation: "pulseMap 9s ease-in-out infinite",
          }}
        />
        <div className="absolute top-[-10%] right-[5%] w-[700px] h-[700px] rounded-full bg-yellow-400/[0.07] blur-[140px]" />
        <div className="absolute top-[55%] left-[-5%] w-[600px] h-[600px] rounded-full bg-yellow-400/[0.05] blur-[130px]" />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--brand)] mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" /> MediaCrypto Intelligence
          </div>
          <h1 className="font-display font-bold text-5xl md:text-[64px]text-[var(--text-primary)] tracking-tight leading-[1.02]">
            The Future of<br />AI-Powered<br /><span className="text-yellow-400">Crypto Intelligence</span>
          </h1>
          <p className="mt-7 text-lg text-[var(--text-secondary)] max-w-md leading-relaxed">
            Track markets, analyze wallets, and get AI-powered insights, all in one platform.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <button
              onClick={() => document.getElementById("featured-tools")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-8 py-4 rounded-full hover:bg-yellow-300 transition text-sm shadow-[0_0_40px_rgba(250,204,21,0.3)]"
            >
              Explore Tools →
            </button>
            <Link to="/news">
              <button className="text-[var(--text-secondary)] font-bold uppercase tracking-wide px-8 py-4 rounded-full hover:text-white transition text-sm border border-[var(--border-soft)]">
                Latest News
              </button>
            </Link>
          </div>
        </div>

        <div className="lg:col-span-6 relative h-[460px] hidden lg:block">
          <FloatingCard label="Bitcoin" value="$63,610" sub="+0.80%" positive style={{ top: "2%", left: "14%" }} />
          <FloatingCard label="Ethereum" value="$1,926" sub="+2.86%" positive style={{ top: "26%", right: "2%", animationDelay: "1.4s" }} />
          <FloatingCard label="AI Summary" value="Fear · 29" style={{ top: "50%", left: "0%", animationDelay: "2.3s" }} />
          <FloatingCard label="Wallet Scanner" value="Low Risk" style={{ top: "64%", right: "16%", animationDelay: "0.7s" }} />
          <FloatingCard label="Portfolio" value="Live" style={{ top: "84%", left: "26%", animationDelay: "2.0s" }} />
        </div>
      </div>

      <style>{`
        @keyframes floatY { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-16px); } }
        @keyframes pulseMap { 0%,100% { opacity: 0.36; } 50% { opacity: 0.50; } }
      `}</style>
    </section>
  );
}

function AISection() {
  const { theme } = useTheme();
  const features = [
    { label: "AI Wallet Analysis", live: false, soon: true },
    { label: "AI Market Summary", live: true },
    { label: "AI News Summary", live: false, soon: true },
    { label: "AI Portfolio Insights", live: false, soon: true },
    { label: "AI Community Recap", live: false, soon: true },
    { label: "AI Scam Detection", live: false, soon: true },
  ];

  return (
    <section className="relative pt-16 pb-16 overflow-hidden bg-[var(--bg-base)]">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-yellow-400/[0.05] blur-[150px] pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative flex items-center justify-center order-2 lg:order-1">
            <div className="absolute w-96 h-96 rounded-full bg-yellow-400/[0.10] blur-[120px]" />
            <img
              src={theme === "light" ? "/images/ai-robot-light.png" : "/images/ai-robot.png"}
              alt="AI Crypto Intelligence"
              className="relative w-full max-w-[420px] lg:-ml-10 drop-shadow-[0_0_60px_rgba(250,204,21,0.25)]"
              style={{ animation: "floatY 6s ease-in-out infinite" }}
            />
          </div>

          <Reveal className="order-1 lg:order-2">
            <div className="text-xs font-mono uppercase tracking-widest text-[var(--brand)] mb-4">AI Center</div>
            <h2 className="font-display font-bold text-4xl md:text-5xltext-[var(--text-primary)] tracking-tight leading-[1.05]">AI Crypto<br />Intelligence</h2>
            <p className="mt-5 text-[var(--text-secondary)] text-lg">Let AI understand the crypto market for you.</p>
            <ul className="mt-9 space-y-4">
              {features.map((f) => (
                <li key={f.label} className="flex items-center gap-3">
                  <span className={"w-1.5 h-1.5 rounded-full flex-shrink-0 " + (f.live ? "bg-green-400" : "bg-[var(--border-soft)]")} />
                  <span className={f.live ? "text-white" : "text-[var(--text-tertiary)]"}>{f.label}</span>
                  {f.live && <span className="ml-auto text-xs font-mono text-[var(--bullish)] font-bold">Live</span>}
                  {f.soon && <span className="ml-auto text-xs font-mono text-[var(--text-tertiary)]">Coming soon</span>}
                </li>
              ))}
            </ul>
            <Link to="/tools/market-summary">
              <button className="mt-10 bg-[var(--brand)] text-black font-bold uppercase tracking-wide px-8 py-4 rounded-full hover:bg-[var(--brand-hover)] transition text-sm">
                Try AI →
              </button>
            </Link>
          </Reveal>
        </div>
      </div>

      <style>{`
        @keyframes floatY { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-16px); } }
      `}</style>
    </section>
  );
}

/* ============================================================
   TOP MARKETS — compact
============================================================ */
function Sparkline({ points, positive }) {
  if (!points || points.length < 2) return <div className="w-20 h-8" />;
  const min = Math.min(...points), max = Math.max(...points), range = max - min || 1;
  const w = 84, h = 30, step = w / (points.length - 1);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${i * step} ${h - ((p - min) / range) * h}`).join(" ");
  return <svg width={w} height={h}><path d={path} fill="none" stroke={positive ? "#4ade80" : "#f87171"} strokeWidth="1.5" /></svg>;
}



const COINGECKO_TO_SLUG = {
  bitcoin: "bitcoin", ethereum: "ethereum", binancecoin: "bnb", ripple: "xrp",
  solana: "solana", cardano: "cardano", dogecoin: "dogecoin", tron: "tron",
  "avalanche-2": "avalanche", polkadot: "polkadot", chainlink: "chainlink",
  litecoin: "litecoin", "matic-network": "polygon", uniswap: "uniswap",
  near: "near", cosmos: "atom", arbitrum: "arbitrum", optimism: "optimism",
  "injective-protocol": "injective", sui: "sui", aptos: "aptos", filecoin: "filecoin",
  stellar: "stellar", vechain: "vechain", algorand: "algorand", "the-sandbox": "sandbox",
  decentraland: "mana", aave: "aave", "render-token": "render", pepe: "pepe",
  "the-open-network": "ton", "shiba-inu": "shiba-inu", "hedera-hashgraph": "hedera",
  "bitcoin-cash": "bitcoin-cash", floki: "floki", dogwifcoin: "dogwifhat", bonk: "bonk",
  jasmycoin: "jasmy", hyperliquid: "hyperliquid", fantom: "fantom", "theta-token": "theta",
  eos: "eos", zilliqa: "zilliqa", kava: "kava", "pancakeswap-token": "pancakeswap",
  gala: "gala", "axie-infinity": "axie-infinity", "the-graph": "the-graph",
  "quant-network": "quant", "immutable-x": "immutable", blockstack: "stacks",
  maker: "maker", "compound-governance-token": "compound", "curve-dao-token": "curve-dao",
  "1inch": "1inch", enjincoin: "enjin", chiliz: "chiliz", blur: "blur", dydx: "dydx",
  "woo-network": "woo", "band-protocol": "band", "ocean-protocol": "ocean", ankr: "ankr",
  skale: "skale", "lido-dao": "lido-dao", "rocket-pool": "rocket-pool", apecoin: "ape",
  stepn: "gmt", magic: "magic", zelcash: "flux", storj: "storj", arweave: "arweave",
  helium: "helium", iota: "iota", neo: "neo", waves: "waves", dash: "dash", zcash: "zcash",
  ontology: "ontology", ravencoin: "ravencoin", harmony: "harmony", celo: "celo",
  icon: "icon", "nervos-network": "nervos", "klay-token": "klaytn", "convex-finance": "convex",
  "yearn-finance": "yearn", sushi: "sushi", balancer: "balancer", loopring: "loopring",
  "mask-network": "mask", "ethereum-name-service": "ethereum-name-service", "sei-network": "sei",
  "trust-wallet-token": "trust-wallet", "jupiter-exchange-solana": "jupiter",
  "pyth-network": "pyth", "worldcoin-wld": "worldcoin", notcoin: "notcoin",
};

function TopMarkets() {
  const [coins, setCoins] = useState([]);
  useEffect(() => {
    fetch("https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=8&page=1&sparkline=true&price_change_percentage=24h,7d")
      .then((r) => r.json()).then((d) => Array.isArray(d) && setCoins(d)).catch(() => {});
  }, []);
  if (coins.length === 0) return null;

  return (
    <section className="pt-4 pb-24 bg-[var(--bg-panel)]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-display font-bold text-3xltext-[var(--text-primary)] tracking-tight">Top Markets</h2>
            <Link to="/coins" className="text-xs font-mono uppercase tracking-widest text-[var(--brand)] hover:text-yellow-400">View All →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {coins.map((c) => {
              const chg = c.price_change_percentage_24h_in_currency ?? 0;
              return (
                <Link key={c.id} to={"/coins/" + (COINGECKO_TO_SLUG[c.id] || c.id)} className="flex items-center justify-between bg-[var(--bg-panel)] hover:bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-xl px-4 py-3.5 transition">
                  <div className="flex items-center gap-3">
                    <img src={c.image} alt="" className="w-7 h-7 rounded-full" />
                    <div>
                      <div className="text-sm font-boldtext-[var(--text-primary)]">{c.symbol.toUpperCase()}</div>
                      <div className="text-xs font-mono text-[var(--text-tertiary)]">${c.current_price < 1 ? c.current_price.toFixed(4) : c.current_price.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <Sparkline points={c.sparkline_in_7d?.price?.slice(-24)} positive={chg >= 0} />
                    <div className={"text-xs font-mono font-bold " + (chg >= 0 ? "text-[var(--bullish)]" : "text-[var(--bearish)]")}>{chg >= 0 ? "+" : ""}{chg.toFixed(2)}%</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ============================================================
   FEATURED TOOLS — true bento grid, Wallet Scanner 2x, Portfolio secondary hero
============================================================ */
function FeaturedTools() {
  const { user } = useAuth();
  return (
    <section id="featured-tools" className="py-32 relative overflow-hidden">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-yellow-400/[0.03] blur-[160px] pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative">
        <Reveal>
          <div className="mb-14">
            <div className="text-xs font-mono uppercase tracking-widest text-yellow-400/80 mb-3">Product Suite</div>
            <h2 className="font-display font-bold text-4xl md:text-5xltext-[var(--text-primary)] tracking-tight">Featured Tools</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
            {/* WALLET SCANNER — flagship, spans 2 cols + taller */}
            <Link to="/tools/wallet-scanner"
              className="lg:col-span-2 lg:row-span-2 group relative rounded-3xl border border-yellow-400/20 bg-gradient-to-br from-yellow-400/[0.08] to-transparent p-8 flex flex-col justify-between min-h-[320px] overflow-hidden hover:border-yellow-400/40 transition">
              <div className="absolute -right-10 -bottom-10 w-56 h-56 rounded-full bg-yellow-400/10 blur-3xl" />
              <div className="relative">
                <span className="text-xs font-mono uppercase tracking-widest text-yellow-400 bg-yellow-400/10 px-2.5 py-1 rounded-full">Flagship</span>
                <div className="w-14 h-14 rounded-2xl bg-yellow-400/15 flex items-center justify-center mt-6">
                  <ShieldCheck className="w-7 h-7 text-yellow-400" />
                </div>
                <div className="font-display font-bold text-2xltext-[var(--text-primary)] mt-5">Wallet Scanner</div>
                <div className="text-[var(--text-secondary)] mt-2 max-w-xs">Scan any wallet across multiple chains for scam, phishing, and risk flags before you sign.</div>
              </div>
              <div className="relative flex items-center gap-2 text-yellow-400 font-mono text-sm font-bold mt-8">
                Scan Now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
              </div>
            </Link>

            {/* PORTFOLIO TRACKER — secondary hero, spans 2 cols */}
            <Link to={user ? "/portfolio" : "/register"}
              className="lg:col-span-2 group relative rounded-3xl border border-[var(--border-soft)] bg-[var(--bg-panel)] p-7 flex items-center justify-between overflow-hidden hover:border-[var(--border-soft)] transition min-h-[150px]">
              <div className="absolute -right-6 -top-6 w-40 h-40 rounded-full bg-green-400/[0.08] blur-2xl" />
              <div className="relative">
                <div className="w-11 h-11 rounded-xl bg-green-400/15 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-[var(--bullish)]" />
                </div>
                <div className="font-display font-bold text-xltext-[var(--text-primary)] mt-4">Portfolio Tracker</div>
                <div className="text-[var(--text-secondary)] text-sm mt-1.5">Track holdings in real time, free</div>
              </div>
              <ArrowRight className="w-5 h-5 text-[var(--text-tertiary)] group-hover:text-white group-hover:translate-x-1 transition relative" />
            </Link>

            {/* smaller secondary tools */}
            {[
              { to: "/tools/whale-tracker", icon: Fish, name: "Whale Tracker", color: "#a78bfa" },
              { to: "/tools/gas-tracker", icon: Fuel, name: "Gas Tracker", color: "#22d3ee" },
              { to: "/tools/profit-calculator", icon: Calculator, name: "Profit Calculator", color: "#facc15" },
              { to: "/tools/dca-calculator", icon: Repeat, name: "DCA Calculator", color: "#f472b6" },
              { to: "/coins", icon: LineChart, name: "Top 100 Coins", color: "#38bdf8" },
              { to: "/tools/market-summary", icon: Sparkles, name: "AI Market Summary", color: "#c084fc", ai: true },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <Link key={t.name} to={t.to}
                  className="group relative rounded-2xl border border-[var(--border-soft)] bg-[var(--bg-panel)] p-5 hover:bg-[var(--bg-elevated)] hover:-translate-y-1 transition">
                  {t.ai && <span className="absolute top-3 right-3 text-[9px] font-mono font-bold text-purple-300 bg-purple-500/15 px-1.5 py-0.5 rounded">AI</span>}
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: t.color + "1f" }}>
                    <Icon className="w-4 h-4" style={{ color: t.color }} />
                  </div>
                  <div className="font-bold text-smtext-[var(--text-primary)] mt-3">{t.name}</div>
                  <ArrowRight className="w-3.5 h-3.5 text-[var(--text-tertiary)] group-hover:text-yellow-400 mt-3 group-hover:translate-x-1 transition" />
                </Link>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ============================================================
   NEWS — compact
============================================================ */
function LatestNews({ news }) {
  if (news.length === 0) return null;
  const [featured, ...small] = news.slice(0, 5);
  return (
    <section className="py-24 bg-[var(--bg-panel)]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-display font-bold text-3xltext-[var(--text-primary)] tracking-tight">Latest News</h2>
            <Link to="/news" className="text-xs font-mono uppercase tracking-widest text-[var(--brand)] hover:text-yellow-400">View All →</Link>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            <Link to={"/news/" + (featured.slug || featured.id)} className="lg:col-span-3 group rounded-2xl overflow-hidden border border-[var(--border-soft)] hover:border-[var(--border-soft)] transition">
              <Thumb n={featured} className="w-full h-72" />
              <div className="p-6 bg-[var(--bg-panel)]">
                <span className="text-xs font-mono text-[var(--brand)] uppercase tracking-wide">{featured.category_slug?.replace(/-/g, " ")}</span>
                <h3 className="font-display font-bold text-2xltext-[var(--text-primary)] group-hover:text-yellow-400 transition mt-2 leading-snug">{featured.title}</h3>
                <div className="text-xs font-mono text-[var(--text-tertiary)] mt-3">{timeAgo(featured.created_at)}</div>
              </div>
            </Link>
            <div className="lg:col-span-2 space-y-3">
              {small.map((n) => (
                <Link key={n.id} to={"/news/" + (n.slug || n.id)} className="flex items-center gap-3 border border-[var(--border-soft)] rounded-xl p-3 hover:border-[var(--border-soft)] hover:bg-[var(--bg-panel)] transition group">
                  <Thumb n={n} className="w-14 h-14 rounded-lg flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-smtext-[var(--text-primary)] group-hover:text-yellow-400 transition line-clamp-2 leading-snug">{n.title}</div>
                    <div className="text-xs font-mono text-[var(--text-tertiary)] mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ============================================================
   MARKET PULSE — count-up stats
============================================================ */
function StatItem({ label, target, suffix = "", color, decimals = 0 }) {
  const [ref, val] = useCountUp(target);
  return (
    <div ref={ref} className="text-center">
      <div className="font-display font-bold text-4xl" style={{ color }}>{decimals ? val.toFixed(decimals) : val}{suffix}</div>
      <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mt-2">{label}</div>
    </div>
  );
}

function MarketPulse() {
  const [fg, setFg] = useState(null);
  const [mood, setMood] = useState(null);
  const [gas, setGas] = useState(null);

  useEffect(() => {
    fetch("https://api.alternative.me/fng/?limit=1").then((r) => r.json()).then((d) => setFg(d.data[0])).catch(() => {});
    api.get("/tools/gas-tracker").then((r) => setGas(r.data)).catch(() => {});
    fetch("https://api.binance.com/api/v3/ticker/24hr").then((r) => r.json()).then((data) => {
      const majors = ["BTCUSDT", "ETHUSDT", "SOLUSDT", "XRPUSDT", "BNBUSDT"];
      const filtered = data.filter((d) => majors.includes(d.symbol));
      setMood(filtered.reduce((s, d) => s + parseFloat(d.priceChangePercent), 0) / filtered.length);
    }).catch(() => {});
  }, []);

  return (
    <section className="py-24 relative">
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-yellow-400/[0.03] blur-[140px] pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative">
        <Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 py-10">
            <StatItem label="Fear & Greed" target={fg ? parseInt(fg.value) : 0} color="#facc15" />
            <StatItem label="Market Mood" target={mood != null ? Math.round(mood * 10) : 0} suffix="%" color={mood >= 0 ? "#4ade80" : "#f87171"} decimals={1} />
            <StatItem label="Gas (gwei)" target={gas ? Math.round(gas.propose * 100) : 0} suffix="" color="#22d3ee" decimals={2} />
            <StatItem label="Updated" target={24} suffix="/7" color="#a78bfa" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ============================================================
   COMMUNITY ECOSYSTEM — one unified container: discussions + analysts + poll
============================================================ */
function CommunityEcosystem() {
  const [posts, setPosts] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [poll, setPoll] = useState(null);
  const [voted, setVoted] = useState(false);
  const [votedOption, setVotedOption] = useState(null);
  const [voting, setVoting] = useState(false);

  useEffect(() => {
    api.get("/community/posts?limit=3").then((r) => setPosts(r.data || [])).catch(() => {});
    api.get("/community/leaderboard").then((r) => setLeaders(r.data || [])).catch(() => {});
    api.get("/polls/active").then((r) => {
      setPoll(r.data);
      const saved = localStorage.getItem("poll_voted_" + r.data.id);
      if (saved) { setVoted(true); setVotedOption(saved); }
    }).catch(() => {});
  }, []);

  const vote = async (option) => {
    if (voted || voting || !poll) return;
    setVoting(true);
    try {
      const { data } = await api.post("/polls/" + poll.id + "/vote", { option });
      setPoll(data); setVoted(true); setVotedOption(option);
      localStorage.setItem("poll_voted_" + poll.id, option);
    } catch (e) { toast.error("Could not vote"); } finally { setVoting(false); }
  };

  const total = poll ? poll.options.reduce((s, o) => s + o.votes, 0) : 0;
  const medals = ["🥇", "🥈", "🥉"];

  return (
    <section className="py-32 bg-[var(--bg-panel)] relative overflow-hidden">
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full bg-yellow-400/[0.04] blur-[150px] pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative">
        <Reveal>
          <div className="mb-14">
            <div className="text-xs font-mono uppercase tracking-widest text-[var(--brand)] mb-3">Community</div>
            <h2 className="font-display font-bold text-4xl md:text-5xltext-[var(--text-primary)] tracking-tight">One Living Ecosystem</h2>
          </div>

          <div className="rounded-3xl border border-[var(--border-soft)] bg-[var(--bg-panel)] overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-white/8">

              {/* trending discussions */}
              <div className="p-8">
                <div className="flex items-center gap-2 mb-6 text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
                  <MessageCircle className="w-4 h-4 text-yellow-400" /> Trending Discussions
                </div>
                <div className="space-y-4">
                  {posts.map((p) => (
                    <Link key={p.id} to={"/community/" + p.id} className="block group">
                      <div className="text-smtext-[var(--text-primary)] group-hover:text-yellow-400 transition line-clamp-2">{p.title}</div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs font-mono text-[var(--text-tertiary)]">
                        <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{p.comments_count || 0}</span>
                        <span className="flex items-center gap-1"><Flame className="w-3 h-3" />{p.likes_count || 0}</span>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link to="/community" className="inline-block mt-6 text-xs font-mono text-yellow-400 hover:underline">Join the Discussion →</Link>
              </div>

              {/* top analysts */}
              <div className="p-8">
                <div className="flex items-center gap-2 mb-6 text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
                  <BarChart2 className="w-4 h-4 text-yellow-400" /> Top Analysts
                </div>
                <div className="space-y-3">
                  {leaders.slice(0, 3).map((l, i) => (
                    <Link key={l.author_id} to={"/profile/" + l.author_id} className="flex items-center justify-between group">
                      <div className="flex items-center gap-2.5">
                        <span className="text-sm w-5">{i < 3 ? medals[i] : i + 1}</span>
                        {l.author_avatar ? <img src={l.author_avatar} className="w-7 h-7 rounded-full object-cover" alt="" /> : (
                          <div className="w-7 h-7 rounded-full bg-yellow-400/10 flex items-center justify-center"><span className="text-yellow-400 text-xs font-bold">{l.author_name?.[0]}</span></div>
                        )}
                        <span className="text-sm text-[var(--text-secondary)] group-hover:text-white transition truncate max-w-[90px]">{l.author_name}</span>
                      </div>
                      <span className="text-xs font-mono font-bold text-yellow-400">{l.accuracy}%</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* weekly poll */}
              {poll && (
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-6 text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
                    <Zap className="w-4 h-4 text-yellow-400" /> Weekly Poll
                  </div>
                  <div className="text-sm font-boldtext-[var(--text-primary)] mb-4 leading-snug">{poll.question}</div>
                  <div className="space-y-2">
                    {poll.options.map((o) => {
                      const pct = total > 0 ? Math.round((o.votes / total) * 100) : 0;
                      const isMine = votedOption === o.label;
                      return (
                        <button key={o.label} onClick={() => vote(o.label)} disabled={voted || voting}
                          className={"w-full relative overflow-hidden rounded-lg border transition text-left " + (isMine ? "border-l-[3px] border-l-yellow-400 border-y border-r border-y-white/10 border-r-white/10" : "border border-[var(--border-soft)]") + " disabled:cursor-default"}>
                          <div className="absolute inset-0 bg-yellow-400/10" style={{ width: pct + "%" }} />
                          <div className="relative flex items-center justify-between px-3 py-2 text-xs gap-2">
                            <span className="text-white/80">{o.label}</span>
                            {voted && <span className="font-mono font-bold text-yellow-400 flex-shrink-0">{pct}%</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-4 text-xs font-mono text-[var(--text-tertiary)]">{total.toLocaleString()} votes · updates every 30s</div>
                </div>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ============================================================
   AI MARKET BRIEF
============================================================ */
function AIMarketBrief({ news }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/tools/market-summary").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const topNews = news[0];

  return (
    <section className="py-32 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-yellow-400/[0.05] blur-[150px] pointer-events-none" />
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 relative text-center">
        <Reveal>
          <div className="w-14 h-14 rounded-2xl bg-yellow-400/15 flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-6 h-6 text-yellow-400" />
          </div>
          <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-3">Updated Hourly</div>
          <h2 className="font-display font-bold text-4xltext-[var(--text-primary)] tracking-tight mb-6">Today's AI Market Brief</h2>
          <p className="text-[var(--text-secondary)] text-lg leading-relaxed max-w-2xl mx-auto">{data.summary}</p>
          {topNews && (
            <Link to={"/news/" + (topNews.slug || topNews.id)} className="inline-flex items-center gap-2 mt-8 text-sm text-yellow-400 hover:underline">
              <Newspaper className="w-4 h-4" /> {topNews.title}
            </Link>
          )}
        </Reveal>
      </div>
    </section>
  );
}


function StatsStrip({ stats }) {
  const [articlesRef, articlesVal] = useCountUp(stats?.articles);
  const [toolsRef, toolsVal] = useCountUp(stats?.tools);
  if (!stats) return null;
  return (
    <section className="py-14 border-t border-b border-[var(--border-soft)]">
      <div className="max-w-[1200px] mx-auto px-6 flex flex-wrap justify-center items-center gap-12 md:gap-16">
        <div ref={articlesRef} className="text-center">
          <div className="font-display font-bold text-3xl text-[var(--brand)]">{articlesVal}+</div>
          <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mt-1">Articles</div>
        </div>
        <div ref={toolsRef} className="text-center">
          <div className="font-display font-bold text-3xl text-[var(--brand)]">{toolsVal}+</div>
          <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mt-1">Free Tools</div>
        </div>
        <div className="text-center">
          <div className="font-display font-bold text-3xl text-[var(--text-primary)]">24/7</div>
          <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mt-1">Market Updates</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--bullish)] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[var(--bullish)]"></span>
            </span>
            <span className="font-display font-bold text-xl text-[var(--text-primary)]">Live</span>
          </div>
          <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mt-1">Community</div>
        </div>
      </div>
    </section>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[var(--border-soft)] py-5">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left">
        <span className="font-bold text-[var(--text-primary)]">{q}</span>
        <span className="text-[var(--brand)] text-xl flex-shrink-0 ml-4">{open ? "−" : "+"}</span>
      </button>
      {open && <p className="text-[var(--text-secondary)] text-sm mt-3 leading-relaxed">{a}</p>}
    </div>
  );
}

function FAQ() {
  const items = [
    { q: "Is MediaCrypto free to use?", a: "Yes. All news, tools, and community features are completely free, no subscription or paywall." },
    { q: "Who runs MediaCrypto?", a: "MediaCrypto is an independently run crypto news and tools platform. We publish original analysis and build our own tools, with no outside editorial influence on coverage." },
    { q: "How accurate are community predictions?", a: "Every prediction is tracked publicly and resolved automatically using real market data at the target date, no cherry-picking. Accuracy percentages on each profile reflect a user's real track record." },
    { q: "Do you give financial advice?", a: "No. MediaCrypto provides news, data, and tools for informational purposes only. Nothing on this site should be taken as financial advice, always do your own research." },
        { q: "How does MediaCrypto use AI?", a: "AI-assisted tools may support research organization, data processing, and draft preparation across some of our content, including features like the AI Market Summary. AI does not determine our editorial conclusions or opinions. Published articles are reviewed by the MediaCrypto editorial team, and material financial, regulatory, and factual claims are checked against authoritative sources before publication. See our full Editorial Policy for details." },
  ];
  return (
    <section className="py-24">
      <div className="max-w-[800px] mx-auto px-6">
        <div className="text-xs font-mono uppercase tracking-widest text-[var(--brand)] mb-3 text-center">FAQ</div>
        <h2 className="font-display font-bold text-3xl md:text-4xl text-[var(--text-primary)] tracking-tight text-center mb-10">Frequently Asked Questions</h2>
        <div>
          {items.map((item) => <FAQItem key={item.q} q={item.q} a={item.a} />)}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   NEWSLETTER

============================================================ */
function Newsletter() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Enter a valid email");
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      setDone(true);
      toast.success("Subscribed!");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not subscribe"); } finally { setLoading(false); }
  };

  return (
    <section className="py-28 bg-[var(--bg-panel)]">
      <div className="max-w-lg mx-auto px-6 text-center">
        <Reveal>
          <Mail className="w-7 h-7 text-yellow-400 mx-auto mb-5" />
          <h2 className="font-display font-bold text-3xltext-[var(--text-primary)]">Get the Daily AI Crypto Brief</h2>
          <p className="mt-3 text-[var(--text-tertiary)] text-sm">One email a day. Market summary, top movers, top stories.</p>
          {done ? (
            <div className="mt-8 text-[var(--bullish)] font-mono text-sm">✓ Subscribed</div>
          ) : (
            <form onSubmit={subscribe} className="mt-8 flex gap-2">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email"
                className="flex-1 bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-full px-5 py-3 text-smtext-[var(--text-primary)] focus:outline-none focus:border-yellow-400/50" />
              <button type="submit" disabled={loading} className="bg-[var(--brand)] text-black font-bold uppercase tracking-wide px-6 py-3 rounded-full hover:bg-[var(--brand-hover)] transition text-xs disabled:opacity-50 flex items-center gap-2 whitespace-nowrap">
                {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Subscribe
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}




function StocksWidget() {
  const [stocks, setStocks] = useState([]);
  useEffect(() => {
    api.get("/stocks/list").then((r) => setStocks(r.data || [])).catch(() => {});
  }, []);
  if (stocks.length === 0) return null;
  const top = [...stocks].sort((a, b) => Math.abs(b.change_pct) - Math.abs(a.change_pct)).slice(0, 5);

  return (
    <div className="bg-white/[0.02] border border-white/8 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-white">Stocks</h3>
        <Link to="/stocks" className="text-xs font-mono text-yellow-400/80 hover:text-yellow-400">View All →</Link>
      </div>
      <div className="space-y-1">
        {top.map((s) => {
          const up = s.change_pct >= 0;
          return (
            <Link key={s.symbol} to={"/stocks/" + s.symbol} className="flex items-center justify-between py-2 px-2 -mx-2 rounded-lg hover:bg-white/[0.03] transition">
              <div>
                <div className="text-sm font-bold text-white">{s.symbol}</div>
                <div className="text-xs text-white/40">{s.name}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-sm text-white">${s.price?.toFixed(2)}</div>
                <div className={"text-xs font-mono font-bold " + (up ? "text-green-400" : "text-red-400")}>
                  {up ? "+" : ""}{s.change_pct?.toFixed(2)}%
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function ForexWidget() {
  const [pairs, setPairs] = useState([]);
  useEffect(() => {
    api.get("/forex/pairs").then((r) => setPairs(r.data || [])).catch(() => {});
  }, []);
  if (pairs.length === 0) return null;
  const top = pairs.slice(0, 5);

  return (
    <div className="bg-white/[0.02] border border-white/8 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-white">Forex</h3>
        <Link to="/forex" className="text-xs font-mono text-yellow-400/80 hover:text-yellow-400">View All →</Link>
      </div>
      <div className="space-y-1">
        {top.map((p) => {
          const up = p.change_pct >= 0;
          const slug = p.pair.replace("/", "-");
          return (
            <Link key={p.pair} to={"/forex/" + slug} className="flex items-center justify-between py-2 px-2 -mx-2 rounded-lg hover:bg-white/[0.03] transition">
              <div>
                <div className="text-sm font-bold text-white">{p.pair}</div>
                <div className="text-xs text-white/40">{p.name}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-sm text-white">{p.rate?.toFixed(4)}</div>
                {p.change_pct != null && (
                  <div className={"text-xs font-mono font-bold " + (up ? "text-green-400" : "text-red-400")}>
                    {up ? "+" : ""}{p.change_pct.toFixed(2)}%
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function GlobalMarkets() {
  return (
    <section className="py-24">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-display font-bold text-3xl text-white tracking-tight">Global Markets</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <StocksWidget />
          <ForexWidget />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
          <CryptoCalendarWidget />
          <EconomicCalendarWidget />
          <TradingSimWidget />
        </div>
        <div className="w-full flex justify-center items-center">
          <AdBanner adKey="446007b2fa20c3b18764f3aed029d17e" width={468} height={60} />
        </div>

      </div>
    </section>
  );
}




function CryptoCalendarWidget() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/calendar/events", { params: { category: "all", upcoming_only: true, limit: 5 } })
      .then((r) => setEvents((r.data || []).filter((e) => e.category !== "macro").slice(0, 5)))
      .catch(() => {});
  }, []);
  if (events.length === 0) return null;

  const catColor = { unlock: "#f87171", airdrop: "#4ade80" };

  return (
    <div className="bg-white/[0.02] border border-white/8 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-white">Crypto Calendar</h3>
        <Link to="/calendar" className="text-xs font-mono text-yellow-400/80 hover:text-yellow-400">View All →</Link>
      </div>
      <div className="space-y-3">
        {events.map((ev) => (
          <div key={ev.id} className="flex items-start gap-3">
            <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: catColor[ev.category] || "#facc15" }} />
            <div className="min-w-0">
              <div className="text-sm text-white truncate">{ev.title}</div>
              <div className="text-xs text-white/40 font-mono">
                {new Date(ev.event_date + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                {ev.asset && " · " + ev.asset}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EconomicCalendarWidget() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/calendar/events", { params: { category: "macro", upcoming_only: true, limit: 5 } })
      .then((r) => setEvents((r.data || []).slice(0, 5)))
      .catch(() => {});
  }, []);
  if (events.length === 0) return null;

  const impactColor = { high: "#f87171", medium: "#fb923c", low: "#facc15" };

  return (
    <div className="bg-white/[0.02] border border-white/8 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-white">Economic Calendar</h3>
        <Link to="/economic-calendar" className="text-xs font-mono text-yellow-400/80 hover:text-yellow-400">View All →</Link>
      </div>
      <div className="space-y-3">
        {events.map((ev) => (
          <div key={ev.id} className="flex items-start gap-3">
            <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: impactColor[ev.impact] || "#facc15" }} />
            <div className="min-w-0">
              <div className="text-sm text-white truncate">{ev.title}</div>
              <div className="text-xs text-white/40 font-mono">
                {new Date(ev.event_date + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                {ev.currency && " · " + ev.currency}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



function TradingSimWidget() {
  const [leaders, setLeaders] = useState([]);
  useEffect(() => {
    api.get("/monthly-challenge/leaderboard").then((r) => setLeaders((r.data || []).slice(0, 5))).catch(() => {});
  }, []);
  if (leaders.length === 0) return null;
  const medals = ["🥇", "🥈", "🥉"];

  return (
    <div className="bg-white/[0.02] border border-white/8 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-white">Trading Simulator</h3>
        <Link to="/trading-simulator" className="text-xs font-mono text-yellow-400/80 hover:text-yellow-400">Play Now →</Link>
      </div>
      <div className="space-y-1">
        {leaders.map((l, i) => {
          const up = l.return_pct >= 0;
          return (
            <Link key={l.user_id} to={"/profile/" + l.user_id} className="flex items-center justify-between py-2 px-2 -mx-2 rounded-lg hover:bg-white/[0.03] transition">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="text-sm w-5 flex-shrink-0">{i < 3 ? medals[i] : "#" + (i + 1)}</span>
                {l.avatar_url ? (
                  <img src={l.avatar_url} alt={l.name} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-yellow-400 font-bold text-xs">{l.name[0]}</span>
                  </div>
                )}
                <span className="text-sm text-white truncate">{l.name}</span>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-mono text-xs text-white">${l.total_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                <div className={"font-mono text-xs font-bold " + (up ? "text-green-400" : "text-red-400")}>
                  {up ? "+" : ""}{l.return_pct}%
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}




/* ============================================================
   HOME
============================================================ */
export default function Home() {
  const [news, setNews] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/news?limit=20").then((r) => setNews(r.data || [])).catch(() => {});
    api.get("/stats/site").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-[var(--bg-base)] overflow-x-hidden">
      {ADS_ENABLED && (
        <div className="flex justify-center py-4 border-b border-[var(--border-soft)]" style={{ background: "var(--bg-panel)" }}>
          <AdBanner adKey="dc8c659b65e8c76e1b0272f7555e95da" width={728} height={90} />
        </div>
      )}
      <Hero />
 
      <LatestNews news={news} />
      <AISection />
      <TopMarkets />
      <GlobalMarkets />
      <section className="py-16 bg-[var(--bg-panel)]">
        <div className="max-w-[600px] mx-auto px-6">
          <DailyCall />
        </div>
      </section>
      <FeaturedTools />
      <MarketPulse />
      <CommunityEcosystem />
      <AIMarketBrief news={news} />
      <StatsStrip stats={stats} />
      <FAQ />
      <Newsletter />
    </div>
  );
}
